/* eslint-disable @typescript-eslint/no-explicit-any */

import {FormGroup} from '@angular/forms';
import {IDynamicObject} from '@app/core/models/dynamic-object';
import {ObjectUtils} from '@app/core/utils/object.utils';
import {TillTwoDigits_Regex} from '../constants';
import {EDynamicFormFieldType, IDynamicFormFieldType} from '../models/dynamic.model';
import {DynamicFormBuilderUtils} from './dynamic-form-builder.utils';
import {DynamicFormUtils} from './dynamic-form.utils';

export class DynamicFormValuesUtils {
  static setFormValues(value: string, formTemplateList: IDynamicFormFieldType[], dynamicForm: FormGroup) {
    if (dynamicForm?.controls) {
      const parsedValue = this.prepareParsedValue(value);
      this.mapAndSetFormValues(parsedValue, formTemplateList, dynamicForm);
      dynamicForm.patchValue(parsedValue);
    }
  }

  static setTemplateValues(value: string, formTemplateList: IDynamicFormFieldType[]) {
    const parsedValue = this.prepareParsedValue(value);
    this.mapAndSetTemplateValues(parsedValue, formTemplateList);
  }

  static prepareParsedValue(value: string) {
    const hasHpoInOther = value.includes('clinicalHistory.otherAnomalies.hpo');
    let parsedValue = JSON.parse(value);
    parsedValue = ObjectUtils.convertDotKeysToObjects(parsedValue);
    parsedValue = this.convertWESOldToNewValuesJson(parsedValue, hasHpoInOther);
    return parsedValue;
  }

  static convertWESOldToNewValuesJson(parsedValue: any, hasHpoInOther: boolean = false) {
    const parentKey = 'clinicalHistory';
    const baseSections = [
      'clinicalDetails',
      'familyHistory',
      'prenatalAnamnesis',
      'brainAbnormalities',
      'developmentalDisorders',
      'craniofacialAbnormalities',
      'metabolicAndEndocrineAbnormalities',
      'hearingDefectsAndSenseDisorders',
      'musculoskeletalDisorders',
      'respiratorySymptoms',
      'cardiovascularDiseases',
      'immunologicalAndHematologicalAbnormalities',
      'skinNailsAndHairAbnormalities',
      'eyeDefects',
      'otherAnomalies',
    ];

    const wesSections = hasHpoInOther
      ? baseSections
      : [...baseSections.slice(0, 2), 'relevantHPOTerms', ...baseSections.slice(2)];

    if (Object.hasOwn(parsedValue, parentKey) && wesSections.some(x => Object.hasOwn(parsedValue[parentKey], x))) {
      const newWESValue: any = {};
      newWESValue[parentKey] = [];
      for (const section of wesSections) {
        const currValue = parsedValue[parentKey][section] ?? {};
        (newWESValue[parentKey] as any[]).push(currValue);
      }
      return newWESValue;
    }
    return parsedValue;
  }

  static mapAndSetFormValues(parsedValue: any, formTemplateList: IDynamicFormFieldType[], dynamicForm: FormGroup) {
    for (let i = 0; i < formTemplateList?.length; i++) {
      const formTemplate = formTemplateList[i];

      switch (formTemplate.type) {
        case EDynamicFormFieldType.Accordion:
        case EDynamicFormFieldType.VariableAccordion:
          this.handleAccordion(parsedValue, formTemplate, dynamicForm);
          break;

        case EDynamicFormFieldType.AccordionTab:
        case EDynamicFormFieldType.VariableAccordionTab:
          this.handleAccordionTab(parsedValue, formTemplate, dynamicForm, i);
          break;

        case EDynamicFormFieldType.InputGroup:
          this.handleInputGroup(parsedValue, formTemplate, dynamicForm);
          break;

        case EDynamicFormFieldType.Date:
          this.handleDate(parsedValue, formTemplate);
          break;

        case EDynamicFormFieldType.Checkbox:
          this.handleCheckbox(parsedValue, formTemplate);
          break;

        case EDynamicFormFieldType.Select:
        case EDynamicFormFieldType.MultiSelect:
          this.handleSelect(parsedValue, formTemplate);
          break;
      }
    }
  }

  private static handleAccordion(parsedValue: any, formTemplate: IDynamicFormFieldType, dynamicForm: FormGroup) {
    if (formTemplate.type === EDynamicFormFieldType.VariableAccordion) {
      const values = parsedValue[formTemplate.name] ?? [];
      for (let j = 1; j < values.length; j++) {
        DynamicFormBuilderUtils.addVariableAccordionTab(dynamicForm, formTemplate);
      }
    }
    const template = DynamicFormUtils.castAsAccordion(formTemplate);
    this.mapAndSetFormValues(parsedValue[formTemplate.name], template.fields, dynamicForm);
  }

  private static handleAccordionTab(
    parsedValue: any,
    formTemplate: IDynamicFormFieldType,
    dynamicForm: FormGroup,
    index: number
  ) {
    const template = DynamicFormUtils.castAsAccordionTab(formTemplate);
    this.mapAndSetFormValues(parsedValue[index], template.fields, dynamicForm);
  }

  private static handleInputGroup(parsedValue: any, formTemplate: IDynamicFormFieldType, dynamicForm: FormGroup) {
    const template = DynamicFormUtils.castAsInputGroup(formTemplate);
    this.mapAndSetFormValues(parsedValue[formTemplate.name], template.fields, dynamicForm);
  }

  private static handleDate(parsedValue: any, formTemplate: IDynamicFormFieldType) {
    const value = parsedValue[formTemplate.name];
    parsedValue[formTemplate.name] = value ? new Date(value) : value;
  }

  private static handleCheckbox(parsedValue: any, formTemplate: IDynamicFormFieldType) {
    const values = parsedValue[formTemplate.name] ?? [];
    for (const value of values) {
      parsedValue[`${formTemplate.name}_${value}`] = [value];
    }
  }

  private static handleSelect(parsedValue: any, formTemplate: IDynamicFormFieldType) {
    const value = ObjectUtils.getValueByKey(parsedValue, formTemplate.name);
    if (formTemplate.type === EDynamicFormFieldType.MultiSelect) {
      const field = DynamicFormUtils.castAsMultiSelect(formTemplate);
      const values = Array.isArray(value) ? value : [];
      field.options = field.lazy ? values : field.options;
    } else {
      const field = DynamicFormUtils.castAsSelect(formTemplate);
      const valueObj = !Array.isArray(value) && value ? [value] : [];
      const values = Array.isArray(value) ? value : valueObj;
      field.options = field.lazy ? values : field.options;
    }
  }

  static getFormValues(value: any, formTemplateList: IDynamicFormFieldType[]) {
    const clonedValue = structuredClone(value) as IDynamicObject;
    const checkboxFields = this.getAllFieldsWithType(formTemplateList, EDynamicFormFieldType.Checkbox);
    const radiobuttonFields = this.getAllFieldsWithType(formTemplateList, EDynamicFormFieldType.RadioButton);
    const fields = [...checkboxFields, ...radiobuttonFields];
    const excludedKeys = fields.map(x => `${x.name}_`);
    return this.removeExcludedKeys(clonedValue, excludedKeys);
  }

  static removeExcludedKeys(obj: any, excludedKeys: string[]): any {
    if (Array.isArray(obj)) {
      return obj.map(item => this.removeExcludedKeys(item, excludedKeys));
    } else if (typeof obj === 'object' && obj !== null) {
      return Object.fromEntries(
        Object.entries(obj)
          .filter(([key]) => !excludedKeys.some(ex => key.startsWith(ex)))
          .map(([key, value]) => [key, this.removeExcludedKeys(value, excludedKeys)])
      );
    }
    return obj;
  }

  static getAllFieldsWithType(fields: IDynamicFormFieldType[], targetType: EDynamicFormFieldType) {
    if (!fields?.length) {
      return [];
    }
    const targetFields: IDynamicFormFieldType[] = [];
    if (fields) {
      fields.forEach(field => {
        if (field.type === targetType) {
          targetFields.push(field);
        } else if (field.type === EDynamicFormFieldType.InputGroup) {
          const grp = DynamicFormUtils.castAsInputGroup(field);
          targetFields.push(...this.getAllFieldsWithType(grp.fields, targetType));
        } else if (
          field.type === EDynamicFormFieldType.Accordion ||
          field.type === EDynamicFormFieldType.VariableAccordion
        ) {
          const acc = DynamicFormUtils.castAsAccordion(field);
          targetFields.push(...this.getAllFieldsWithType(acc.fields, targetType));
        } else if (
          field.type === EDynamicFormFieldType.AccordionTab ||
          field.type === EDynamicFormFieldType.VariableAccordionTab
        ) {
          const accTab = DynamicFormUtils.castAsAccordionTab(field);
          targetFields.push(...this.getAllFieldsWithType(accTab.fields, targetType));
        }
      });
    }
    return targetFields;
  }

  static mapAndSetTemplateValues(parsedValue: any, formTemplateList: IDynamicFormFieldType[]) {
    if (!ObjectUtils.isEmpty(parsedValue)) {
      for (let i = 0; i < formTemplateList?.length; i++) {
        const formTemplate = formTemplateList[i];
        if (formTemplate.type === EDynamicFormFieldType.Accordion) {
          this.handleTemplateAccordion(formTemplate, parsedValue);
        } else if (formTemplate.type === EDynamicFormFieldType.VariableAccordion) {
          this.handleTemplateVariableAccordion(formTemplate, parsedValue);
        } else if (
          formTemplate.type === EDynamicFormFieldType.AccordionTab ||
          formTemplate.type === EDynamicFormFieldType.VariableAccordionTab
        ) {
          this.handleTemplateAccordionTab(formTemplate, parsedValue, i);
        } else if (formTemplate.type === EDynamicFormFieldType.InputGroup) {
          this.handleTemplateInputGroup(formTemplate, parsedValue);
        } else if (formTemplate.type === EDynamicFormFieldType.Date) {
          this.handleTemplateDate(parsedValue, formTemplate);
        } else if (formTemplate.type === EDynamicFormFieldType.Checkbox) {
          formTemplate.value = parsedValue[formTemplate.name] ?? [];
        } else if (formTemplate.type === EDynamicFormFieldType.Select) {
          this.handleTemplateSelect(formTemplate, parsedValue);
        } else if (formTemplate.type === EDynamicFormFieldType.MultiSelect) {
          this.handleTemplateMultiSelect(parsedValue, formTemplate);
        } else {
          formTemplate.value = parsedValue[formTemplate.name];
        }
      }
    }
  }

  private static handleTemplateDate(parsedValue: any, formTemplate: IDynamicFormFieldType) {
    const value = parsedValue[formTemplate.name];
    formTemplate.value = value ? new Date(value) : value;
  }

  private static handleTemplateInputGroup(formTemplate: IDynamicFormFieldType, parsedValue: any) {
    const template = DynamicFormUtils.castAsInputGroup(formTemplate);
    this.mapAndSetTemplateValues(parsedValue[formTemplate.name], template.fields);
  }

  private static handleTemplateAccordionTab(formTemplate: IDynamicFormFieldType, parsedValue: any, i: number) {
    const template = DynamicFormUtils.castAsAccordionTab(formTemplate);
    this.mapAndSetTemplateValues(parsedValue[i], template.fields);
  }

  private static handleTemplateAccordion(formTemplate: IDynamicFormFieldType, parsedValue: any) {
    const template = DynamicFormUtils.castAsAccordion(formTemplate);
    this.mapAndSetTemplateValues(parsedValue[formTemplate.name], template.fields);
  }

  static handleTemplateMultiSelect(parsedValue: any, formTemplate: IDynamicFormFieldType) {
    const values = ObjectUtils.getAllValuesByKey(parsedValue, formTemplate.name);
    const field = DynamicFormUtils.castAsMultiSelect(formTemplate);
    field.options = field.lazy ? values : field.options;
    formTemplate.value = parsedValue[formTemplate.name];
  }

  static handleTemplateSelect(formTemplate: IDynamicFormFieldType, parsedValue: any) {
    const field = DynamicFormUtils.castAsSelect(formTemplate);
    const values = ObjectUtils.getAllValuesByKey(parsedValue, formTemplate.name);
    const valueObj = values && !Array.isArray(values) ? [values] : [];
    const value = Array.isArray(values) ? values : valueObj;
    field.options = field.lazy ? value : field.options;
    formTemplate.value = parsedValue[formTemplate.name];
  }

  static handleTemplateVariableAccordion(formTemplate: IDynamicFormFieldType, parsedValue: any) {
    const template = DynamicFormUtils.castAsVariableAccordion(formTemplate);
    const values = parsedValue[formTemplate.name] ?? [];
    for (let j = 1; j < values.length; j++) {
      if (template.max && template.fields.length < template.max) {
        const initialTab = template.fields[0];
        const nextTabName = initialTab.name.replace(TillTwoDigits_Regex, (template.fields.length + 1).toString());
        const nextTab = JSON.parse(
          JSON.stringify(initialTab)
            .replaceAll(`"name":"${initialTab.name}.`, '"name":"')
            .replaceAll(`${initialTab.name}`, `${nextTabName}`)
        );
        template.fields.push(nextTab);
      }
    }
    this.mapAndSetTemplateValues(parsedValue[formTemplate.name], template.fields);
  }
}
